import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { formatCurrency } from '../../../utils/currency';
import { useCanManageFinance } from '../../../hooks/useFinanceRole';
import { Modal, Btn, INPUT, LABEL, fmtDate, localToday, r2, errMsg } from './ui';

function ApplyModal({ advance, onClose, onDone }) {
  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const params = advance.accountId ? { accountId: advance.accountId._id || advance.accountId } : { contactId: advance.contactId?._id || advance.contactId };
    api.get('/crm/receipts/open-invoices', { params })
      .then((res) => setInvoices((res.data.invoices || []).filter((i) => i.currency === advance.currency)))
      .catch(() => setInvoices([]));
  }, [advance]);
  const inv = invoices.find((i) => i._id === invoiceId);
  const due = inv ? r2(inv.totalAmount - (inv.amountPaid || 0) - (inv.amountCredited || 0)) : 0;
  useEffect(() => { if (inv) setAmount(String(Math.min(due, advance.balance))); }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/crm/receipts/advances/${advance._id}/apply`, { invoiceId, amount: Number(amount) });
      alertService.success('Advance applied');
      onDone();
    } catch (e) { alertService.error('Could not apply', errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Apply advance to an invoice" onClose={onClose}
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving || !invoiceId || !(Number(amount) > 0)} onClick={save}>{saving ? 'Applying…' : 'Apply'}</Btn></>}>
      <p className="text-[13px] text-[#706E6B]">Available: <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(advance.balance, advance.currency)}</strong></p>
      {invoices.length === 0 ? <p className="text-[13px] text-[#706E6B]">This customer has no unpaid {advance.currency} invoices.</p> : (
        <>
          <div>
            <label className={LABEL} htmlFor="adv-inv">Invoice</label>
            <select id="adv-inv" className={INPUT} value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
              <option value="">Choose invoice…</option>
              {invoices.map((i) => <option key={i._id} value={i._id}>{i.invoiceNumber} · due {formatCurrency(r2(i.totalAmount - (i.amountPaid || 0) - (i.amountCredited || 0)), i.currency)}</option>)}
            </select>
          </div>
          <div><label className={LABEL} htmlFor="adv-amt">Amount</label><input id="adv-amt" type="number" min="0" step="0.01" className={INPUT} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        </>
      )}
    </Modal>
  );
}

function RefundModal({ advance, onClose, onDone }) {
  const [amount, setAmount] = useState(String(advance.balance));
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(localToday());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/crm/receipts/advances/${advance._id}/refund`, { amount: Number(amount), reference, date });
      alertService.success('Advance refunded');
      onDone();
    } catch (e) { alertService.error('Could not refund', errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Refund advance" onClose={onClose}
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving || !(Number(amount) > 0)} onClick={save}>{saving ? 'Saving…' : 'Record refund'}</Btn></>}>
      <p className="text-[13px] text-[#706E6B]">Record money paid back to the customer. Available: <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(advance.balance, advance.currency)}</strong></p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={LABEL} htmlFor="ref-amt">Amount</label><input id="ref-amt" type="number" min="0" step="0.01" className={INPUT} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        <div><label className={LABEL} htmlFor="ref-date">Date</label><input id="ref-date" type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} /></div>
      </div>
      <div><label className={LABEL} htmlFor="ref-ref">Reference (UTR / cheque no.)</label><input id="ref-ref" className={INPUT} value={reference} onChange={(e) => setReference(e.target.value)} /></div>
    </Modal>
  );
}

// Open customer advances: money received but not yet applied to an invoice.
export default function AdvancesPanel({ refreshKey, onChanged }) {
  const canFinance = useCanManageFinance();
  const [advances, setAdvances] = useState([]);
  const [applying, setApplying] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const load = useCallback(() => {
    api.get('/crm/receipts/advances', { params: { status: 'open' } }).then((res) => setAdvances(res.data.advances || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const done = () => { setApplying(null); setRefunding(null); load(); onChanged?.(); };
  if (!advances.length) return null;

  return (
    <div className="bg-white border border-[#D8DDE6] rounded shadow-sm mb-4">
      <div className="p-3 border-b border-[#D8DDE6]">
        <h3 className="text-[13px] font-bold text-[#2E2E2E]">Customer advances</h3>
        <p className="text-[12px] text-[#706E6B]">Money received that hasn't been used against an invoice yet.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] sm:min-w-[40rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
            <th className="text-left font-semibold px-4 py-2.5">Received</th><th className="text-left font-semibold px-4 py-2.5">Customer</th>
            <th className="text-left font-semibold px-4 py-2.5">From</th><th className="text-right font-semibold px-4 py-2.5">Available</th><th className="px-4 py-2.5" />
          </tr></thead>
          <tbody>
            {advances.map((a) => (
              <tr key={a._id} className="border-b border-[#F3F2F2]">
                <td className="px-4 py-2.5">{fmtDate(a.date)}</td>
                <td className="px-4 py-2.5">{a.accountId?.name || a.contactId?.name || '—'}</td>
                <td className="px-4 py-2.5 text-[#706E6B]">{a.source === 'overpayment' ? 'Overpayment' : 'Advance receipt'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{formatCurrency(a.balance, a.currency)}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {canFinance && <>
                    <button type="button" onClick={() => setApplying(a)} className="text-[12px] font-semibold text-[#0176D3] mr-3">Apply</button>
                    <button type="button" onClick={() => setRefunding(a)} className="text-[12px] font-semibold text-[#706E6B]">Refund</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {applying && <ApplyModal advance={applying} onClose={() => setApplying(null)} onDone={done} />}
      {refunding && <RefundModal advance={refunding} onClose={() => setRefunding(null)} onDone={done} />}
    </div>
  );
}
